import './index.css';
import logo from './assets/logo.png';
import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from "firebase/app";
import {
  getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  updateProfile, sendPasswordResetEmail,
  sendEmailVerification // Add this
} from "firebase/auth";
import {
  getFirestore, collection, addDoc, updateDoc, doc, setDoc, getDoc,
  onSnapshot, query, orderBy, serverTimestamp
} from "firebase/firestore";
import { Send, Plus, MessageSquare, LogOut, Bot, Menu, Moon, Sun, AlertTriangle, User, X, Copy, Share, Settings, Info, Shield, Mail, ChevronDown, Crown, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import config from './config';
import Subscription from './components/Subscription';

const app = initializeApp(config.firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const provider = new GoogleAuthProvider();

const funnyMessages = [
  "Consulting the ancient scrolls of data...",
  "Brewing a strong cup of filter coffee for my circuits...",
  "Asking the server to hurry up, but it's stuck in traffic...",
  "My brain cells are doing Surya Namaskar for better focus...",
  "Counting the bytes... one, two, Samosa...",
  "Polishing my knowledge of the Himalayas (it's a long way up)...",
  "Fetching data faster than a neighbor asking for your results...",
  "Doing a quick yoga session to align my data chakras..."
];

const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const deviceType = isMobile ? "Mobile" : "Laptop/Desktop";

  let browser = "Unknown";
  if (ua.indexOf("Edg/") > -1) {
    browser = "Edge";
  } else if (ua.indexOf("Chrome/") > -1) {
    browser = "Chrome";
  } else if (ua.indexOf("Firefox/") > -1) {
    browser = "Firefox";
  } else if (ua.indexOf("Safari/") > -1) {
    browser = "Safari";
  }

  return { deviceType, browser };
};

export default function App() {
  const browserSessionId = React.useMemo(() => {
    let id = localStorage.getItem('browserSessionId');
    if (!id) {
      id = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('browserSessionId', id);
    }
    return id;
  }, []);

  const [user, setUser] = useState(null);
  const [input, setInput] = useState("");
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [authError, setAuthError] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentView, setCurrentView] = useState('chat');
  const [activeSubscription, setActiveSubscription] = useState(null);
  const isPremium = !!activeSubscription;
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    setVerificationSent(false); // 1. Reset the verification state at start

    if (!email || !password || (isRegistering && !name)) {
      setAuthError("All fields are required.");
      return;
    }

    setAuthLoading(true);

    try {
      if (isRegistering) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName: name });

        // 2. Send the verification email explicitly
        await sendEmailVerification(userCredential.user);

        // 3. CRITICAL: Sign out immediately so the 'onAuthStateChanged' listener 
        // doesn't log them in before they verify
        await signOut(auth);

        // EXTRA: Trigger GA4 sign_up event for Email flow
        if (window.gtag) {
          window.gtag("event", "sign_up", {
            method: "Email"
          });
          console.log("GA4: Email sign_up event triggered");
        }

        setAuthLoading(false);
        setVerificationSent(true); // 4. This resolves your ESLint warning

        // Show success message and clear it after 10 seconds
        // Show success message and clear it after 10 seconds
        // setAuthError("Verification email sent! Please check your inbox (and spam)."); REMOVED DUPLICATE
        setTimeout(() => {
          setVerificationSent(false);
          setAuthError("");
        }, 10000);

      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);

        // 5. BLOCK LOGIN if email is not verified
        if (!userCredential.user.emailVerified) {
          setAuthError("Email not verified. Please check your inbox & spam folder.");
          await signOut(auth); // Immediately boot them out
          setAuthLoading(false);

          setTimeout(() => setAuthError(""), 6000);
          return;
        }
      }
    } catch (error) {
      const errorCode = error.code || error.message;
      let cleanError = errorCode.replace("Firebase: Error ", "").replace(/[()]/g, "");

      if (errorCode === 'auth/email-already-in-use') {
        cleanError = "This email is already registered. Please sign in instead.";
      }

      setAuthError(cleanError);
      setAuthLoading(false);

      setTimeout(() => {
        setAuthError("");
      }, 5000);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setAuthError("Please enter your email to receive a reset link.");
      return;
    }
    setAuthLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setAuthError("");
      setTimeout(() => {
        setResetSent(false);
      }, 5000);
    } catch (error) {
      setAuthError(error.message);
    }
    finally {
      setAuthLoading(false);
    }
  };
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });
  const [showLimitError, setShowLimitError] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    document.title = "JanBharatAI - Know Bharat Better";

    // Check for query params from Landing Page
    const params = new URLSearchParams(window.location.search);
    const queryParam = params.get('q') || params.get('query');
    if (queryParam) {
      setInput(queryParam);

      // Auto-trigger if user is logged in
      // We need to wait for 'user' confirming to not be null.
      // But this effect runs on mount (empty deps).
      // We should probably move this check to a separate effect dependent on 'user'
    }
  }, []);

  // NEW: Auto-search effect
  useEffect(() => {
    if (user) {
      const params = new URLSearchParams(window.location.search);
      const queryParam = params.get('q') || params.get('query');
      if (queryParam) {
        // Remove param to prevent re-triggering
        const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
        window.history.replaceState({ path: newUrl }, '', newUrl);

        setInput(queryParam);
        // Trigger search with the query
        sendMessage(queryParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    setLoadingText(funnyMessages[Math.floor(Math.random() * funnyMessages.length)]);
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u && u.email) {
        // --- ADD THIS CHECK START ---
        // If the user signed in with email/password and is NOT verified, do not log them in
        const isEmailUser = u.providerData.some(p => p.providerId === 'password');
        if (isEmailUser && !u.emailVerified) {
          setUser(null);
          setAuthLoading(false);
          return; // Stop execution here
        }
        // --- ADD THIS CHECK END ---

        try {
          // 1. Fetch User Location Data
          const geoRes = await fetch('https://ipapi.co/json/');
          const geoData = await geoRes.json();

          const country = geoData.country_name;
          const state = geoData.region;

          // 2. Validate Allowed Countries
          const allowedCountries = ["India", "United States"];
          if (!allowedCountries.includes(country)) {
            alert(`Access Denied: JanBharatAI is currently only available in India and the US. (Detected: ${country})`);
            await signOut(auth);
            setUser(null);
            return;
          }

          // 3. User Active Status Check & Data Save
          const userEmail = u.email.toLowerCase();
          const profileRef = doc(db, "users", userEmail, "login_data", "profile");
          const profileSnap = await getDoc(profileRef);

          if (profileSnap.exists()) {
            const data = profileSnap.data();
            if (data.userActiveStatus === false) {
              alert("Your account has been deactivated. Please contact support.");
              await signOut(auth);
              setUser(null);
              setAuthLoading(false);
              return;
            }
          } else {
            // New user registration - Trigger GA4 sign_up event for non-Email providers (e.g. Google)
            // Email providers are handled in handleEmailAuth
            const isEmailUser = u.providerData.some(p => p.providerId === 'password');
            if (!isEmailUser && window.gtag) {
              window.gtag("event", "sign_up", {
                method: "Google"
              });
              console.log("GA4: Google sign_up event triggered");
            }
          }

          // 4. Save User Data (Refactored)
          // STOPPED saving to root users/{email}

          const { deviceType, browser } = getDeviceInfo();

          // Save to users/{email}/login_data/profile
          await setDoc(profileRef, {
            email: userEmail,
            displayName: u.displayName,
            uid: u.uid,
            lastSeen: serverTimestamp(),
            country: country,
            state: state,
            // NEW FIELDS
            deviceType: deviceType,
            browser: browser,
            userActiveStatus: true, // Default to true if missing, or reaffirm it
            createdAt: u.metadata.creationTime ? u.metadata.creationTime : serverTimestamp(), // Persist creation time
          }, { merge: true });

          // 4. Update Session Data in Sub-Collection
          await setDoc(doc(db, "users", u.email.toLowerCase(), "login_data", "session"), {
            activeSessionId: browserSessionId,
            lastSeen: serverTimestamp()
          }, { merge: true });

          setUser(u);
        } catch (error) {
          console.error("Location verification failed:", error);
          setUser(u);
        }
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [browserSessionId]);

  useEffect(() => {
    if (!user || !user.email) {
      setChats([]);
      return;
    }

    try {
      const email = user.email.toLowerCase();
      // Debug log to verify path
      console.log(`Setting up chats listener for path: users/${email}/user_chats`);

      const q = query(
        collection(db, "users", email, "user_chats"),
        orderBy("createdAt", "desc")
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        setChats(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("Error in chats snapshot listener:", error);
        // Handle permission errors gracefully
        if (error.code === 'permission-denied') {
          console.warn("Permission denied for chats. Check if you are logged in correctly.");
        }
      });
      return () => unsubscribe();
    } catch (err) {
      console.error("Error setting up chats listener:", err);
    }
  }, [user]);

  // Real-time listener to enforce single-device session
  useEffect(() => {
    if (!user) return;
    // Listen to users/{email}/login_data/session
    const sessionDocRef = doc(db, "users", user.email.toLowerCase(), "login_data", "session");
    const unsubscribe = onSnapshot(sessionDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.activeSessionId && data.activeSessionId !== browserSessionId) {
          console.warn("Session invalidated: Logged in from another device.");
          alert("Multiple devices detected. You have been logged out of this session.");
          signOut(auth);
        }
      }
    }, (error) => {
      console.error("Session listener error:", error);
      if (error.code === 'permission-denied') {
        // Suppress alert for permission denied, as it might happen during logout/login transitions
        console.warn("Session check permission denied - likely logged out.");
      }
    });
    return () => unsubscribe();
  }, [user, browserSessionId]);

  // Real-time listener for active subscriptions
  useEffect(() => {
    if (!user || !user.email) {
      setActiveSubscription(null);
      return;
    }

    // Query users/{email}/subscription
    const q = query(
      collection(db, "users", user.email.toLowerCase(), "subscription")
    );

    console.log("Setting up subscription listener for:", user.email.toLowerCase());

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log("Subscription snapshot received, empty:", snapshot.empty);
      try {
        if (!snapshot.empty) {
          const now = new Date();
          const activeSubs = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(sub => {
              const expiry = sub.expiresAt?.toDate ? sub.expiresAt.toDate() : new Date(sub.expiresAt);
              return expiry > now;
            })
            .sort((a, b) => {
              const dateA = a.expiresAt?.toDate ? a.expiresAt.toDate() : new Date(a.expiresAt);
              const dateB = b.expiresAt?.toDate ? b.expiresAt.toDate() : new Date(b.expiresAt);
              return dateB - dateA;
            });

          console.log("Found active subscriptions:", activeSubs.length);
          if (activeSubs.length > 0) {
            setActiveSubscription(activeSubs[0]);
          } else {
            setActiveSubscription(null);
          }
        } else {
          setActiveSubscription(null);
        }
      } catch (err) {
        console.error("Error processing subscription snapshot:", err);
        setActiveSubscription(null);
      }
    }, (error) => {
      console.error("Subscription listener failed. If you haven't subscribed yet, this is normal or might be a permission issue:", error);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (loading) {
      setLoadingText(funnyMessages[Math.floor(Math.random() * funnyMessages.length)]);
    }
  }, [loading]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chats, loading, activeChatId]);

  useEffect(() => {
    const handleResize = () => {
      chatEndRef.current?.scrollIntoView({ behavior: "auto" });
    };
    window.visualViewport?.addEventListener('resize', handleResize);
    return () => window.visualViewport?.removeEventListener('resize', handleResize);
  }, []);

  const activeChat = chats.find(c => c.id === activeChatId) || { messages: [], title: "New Chat" };

  const handleLogin = () => signInWithPopup(auth, provider);
  const handleLogout = () => signOut(auth);

  const createNewChat = () => {
    setActiveChatId(null);
    setInput("");
    setShowLimitError(false);
    setIsSidebarOpen(false);
    setCurrentView('chat');
  };

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newVal = !prev;
      localStorage.setItem('theme', newVal ? 'dark' : 'light');
      return newVal;
    });
  };

  const sendMessage = async (promptOverride = null) => {
    // If override provided as string, use it. Otherwise check input.
    const isOverrideString = typeof promptOverride === 'string';
    const promptValue = isOverrideString ? promptOverride : input;

    if (!promptValue.trim() || loading) return;
    setShowLimitError(false);

    setLoading(true);
    // const promptValue = input; // REPLACED BY promptValue above
    const userMsg = { role: "user", text: promptValue };

    if (!isOverrideString) {
      setInput("");
    }

    try {
      // Get the last 3 messages for context-aware semantic caching
      const context = activeChat.messages.slice(-3).map(m => ({
        role: m.role,
        text: m.text
      }));

      const res = await fetch(`${config.API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: promptValue,
          userEmail: user.email,
          userId: user.uid,
          sessionId: activeChatId ? activeChat.googleSessionId : null,
          browserSessionId: browserSessionId,
          context: context,
          showSources: true
        }),
      });

      const data = await res.json();

      if (data.sessionExpired) {
        alert("Your session has expired or you have logged in from another device.");
        signOut(auth);
        setLoading(false);
        return;
      }

      if (data.limitReached) {
        setShowLimitError(true);
        setLoading(false);
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        return;
      }

      // Create the updated bot message including references
      let replyFromServer = data.reply || "I couldn't generate an answer.";
      const sessionId = data.sessionId || null;

      // 1. Strip any existing "Sources:" or "**Sources:**" section to prevent duplication
      // (Handles cases where sources were hardcoded in the cached answer)
      let botContent = replyFromServer.split(/\n\n(?:\*\* )?Sources: (?: \*\*)?/gi)[0].trim();

      // 2. Append sources as clean, numbered clickable links on a single line
      if (data.sourceUrls && Array.isArray(data.sourceUrls) && data.sourceUrls.length > 0) {
        // Filter out duplicates and empty strings
        const uniqueSources = [...new Set(data.sourceUrls)].filter(url => url);

        if (uniqueSources.length > 0) {
          botContent += "\n\n**Sources:** " + uniqueSources.map((url, idx) => `[[${idx + 1}]](${url})`).join(' ');
        }
      }

      const botMsg = {
        role: "bot",
        text: botContent,
        timestamp: new Date().toISOString() // Response timestamp
      };

      if (!activeChatId) {
        const docRef = await addDoc(collection(db, "users", user.email.toLowerCase(), "user_chats"), {
          userId: user.uid,
          title: promptValue.slice(0, 30) + (promptValue.length > 30 ? "..." : ""),
          messages: [userMsg, botMsg],
          googleSessionId: sessionId,
          senderId: user.uid,
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        });
        setActiveChatId(docRef.id);
      } else {
        await updateDoc(doc(db, "users", user.email.toLowerCase(), "user_chats", activeChatId), {
          messages: [...(activeChat?.messages || []), userMsg, botMsg],
          googleSessionId: sessionId,
          lastMessageAt: serverTimestamp()
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    // 1. Add attribution and clean text for clipboard
    let processedText = text
      .replace(/(\*\* )?Sources: ( \*\*)?/gi, "\nSOURCES:")
      .replace(/^\s*[-*•]\s*\[.*?\]\((.*?)\)/gm, "• $1")
      .replace(/```/g, "")
      .trim();

    // 2. Create HTML version with explicit styling for Google Docs compatibility
    // We wrap the content in a div with a standard font-family
    let htmlContent = `
      <div style="font-family: Arial, sans-serif; font-size: 11pt; color: #000000; line-height: 1.5;">
        <p style="margin-bottom: 12pt;">Copied from <a href="https://www.janbharatai.com">https://www.janbharatai.com</a></p>
        ${processedText.replace(/\n/g, '<br/>')}
      </div>
    `;

    // 3. Handle Tables with explicit borders for rich-text editors
    if (text.includes('|') && text.includes('---')) {
      const lines = text.split('\n');
      const tableRows = lines
        .filter(line => line.trim().startsWith('|') && !line.includes('---'))
        .map(line => {
          const cells = line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
          return `<tr>${cells.map(c => `<td style="border: 1px solid #000; padding: 8px; font-family: Arial, sans-serif;">${c}</td>`).join('')}</tr>`;
        })
        .join('');

      const htmlTable = `<table style="border-collapse: collapse; width: 100%; border: 1px solid #000; margin-top: 10px;">${tableRows}</table>`;
      htmlContent = htmlContent.replace(/\|(.|\n)*\|/g, htmlTable);
    }

    try {
      const clipboardItem = new ClipboardItem({
        "text/plain": new Blob([processedText], { type: "text/plain" }),
        "text/html": new Blob([htmlContent], { type: "text/html" })
      });
      await navigator.clipboard.write([clipboardItem]);
    } catch (err) {
      await navigator.clipboard.writeText(processedText);
    }

    setShowCopyToast(true);
    setTimeout(() => setShowCopyToast(false), 2000);
  };

  const shareMessage = async (text) => {
    // Reuse your optimized copy logic first
    await copyToClipboard(text);

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'JanBharatAI Response',
          text: text,
          url: 'https://www.janbharatai.com'
        });
      } catch (err) {
        console.log('Share cancelled or failed');
      }
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'JanBharatAI',
      text: 'Check out JanBharatAI - Know Bharat Better.',
      url: 'https://www.janbharatai.com'
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy link to clipboard if Share API is not supported
        await navigator.clipboard.writeText('https://www.janbharatai.com');
        alert("Link copied to clipboard! (Sharing not supported on this browser)");
      }
    } catch (err) {
      console.log('Error sharing:', err);
    }
  };

  if (!user) {
    return (
      <div className={`h-[100svh] flex flex-col items-center justify-center px-4 transition-colors duration-300 ${darkMode ? 'dark bg-[#131314] text-white' : 'bg-gray-100 text-black'}`}>
        <div className="mb-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-tr from-blue-500 to-purple-500 rounded-full mb-4 mx-auto animate-pulse" />
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-stone-500 to-green-600 font-display tracking-tight">Welcome to JanBharatAI</h1>
          <p className="text-sm md:text-base font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 via-stone-500 to-green-600 mb-8 font-display tracking-wide">Know Bharat Better.</p>
          <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
            {authLoading ? "Verifying session..." : isRegistering ? "Create your account" : "Sign in to start chatting."}
          </p>
        </div>

        {!authLoading ? (
          <div className="w-full max-w-sm space-y-4 animate-fadeIn">
            {/* EMAIL & PASSWORD FORM */}
            <form onSubmit={handleEmailAuth} className="space-y-3">
              {isRegistering && (
                <input
                  type="text"
                  placeholder="Full Name"
                  className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'} outline-none focus:ring-2 focus:ring-blue-500`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              )}
              <input
                type="email"
                placeholder="Email Address"
                className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'} outline-none focus:ring-2 focus:ring-blue-500`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password"
                className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'} outline-none focus:ring-2 focus:ring-blue-500`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              {/* ADD THE VERIFICATION MESSAGE HERE */}
              {verificationSent && (
                <p className="text-green-500 text-xs text-center font-medium bg-green-500/10 py-3 rounded-lg animate-fadeIn">
                  Verification email sent! Please check your inbox and spam folder & click the link to activate your account.
                </p>
              )}

              {authError && <p className="text-red-500 text-xs text-center font-medium bg-red-500/10 py-2 rounded-lg">{authError}</p>}
              {resetSent && <p className="text-green-500 text-xs text-center font-medium bg-green-500/10 py-2 rounded-lg">If you are a registered user, Password reset link will be sent to your email!. Please check you spam folder as well</p>}

              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all shadow-md active:scale-95"
              >
                {isRegistering ? "Create Account" : "Sign In"}
              </button>
            </form>

            {/* ACTION LINKS */}
            <div className="flex justify-between items-center text-xs px-2">
              <button
                onClick={() => { setIsRegistering(!isRegistering); setAuthError(""); setResetSent(false); }}
                className="text-blue-500 font-bold hover:underline"
              >
                {isRegistering ? "Already have an account? Sign In" : "New here? Register Now"}
              </button>
              {!isRegistering && (
                <button
                  onClick={handlePasswordReset}
                  className="text-gray-500 hover:underline"
                >
                  Forgot Password?
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
              <div className="flex-1 h-px bg-gray-300/30" />
              <span>OR</span>
              <div className="flex-1 h-px bg-gray-300/30" />
            </div>

            {/* GOOGLE LOGIN REMAINS UNTOUCHED */}
            <button
              onClick={handleLogin}
              className={`w-full flex items-center justify-center gap-3 px-8 py-3 rounded-xl font-semibold transition-all shadow-lg ${darkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-[#131314] text-white hover:bg-gray-800'}`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
              Continue with Google
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center animate-fadeIn">
            <div className="relative mb-6">
              <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-purple-500 rounded-full animate-ping opacity-30"></div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <p className={`text-sm md:text-base font-medium text-center transition-all duration-500 animate-pulse ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                {loadingText || "Verifying session..."}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex h-[100svh] transition-colors duration-300 ${darkMode ? 'dark bg-[#131314] text-gray-200' : 'bg-gray-50 text-gray-900'}`}>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col p-4 border-r ${darkMode ? 'bg-[#1e1f20] border-gray-800' : 'bg-gray-50 border-gray-200'}
      `}>
        {/* JanBharatAI Brand Name on top of New Chat button */}
        <div className="mb-4 px-2 hidden md:flex items-center justify-center gap-2">
          <img
            src={logo}
            alt="JanBharatAI Logo"
            className="w-8 h-8"
          />
          <h1 className="text-2xl font-bold text-transparent bg-gradient-to-r from-orange-500 via-stone-500 to-green-600 bg-clip-text font-display tracking-tight">
            JanBharatAI
          </h1>
        </div>

        <div className="flex items-center justify-between mb-8 md:hidden px-2 pt-2">
          <div className="flex items-center gap-2">
            <img
              src={logo}
              alt="JanBharatAI Logo"
              className="w-7 h-7"
            />
            <span className="text-xl font-bold text-transparent bg-gradient-to-r from-orange-500 via-stone-500 to-green-600 bg-clip-text font-display tracking-tight">
              JanBharatAI
            </span>
          </div>
          <X className="ml-auto cursor-pointer" onClick={() => setIsSidebarOpen(false)} />
        </div>

        <button
          onClick={createNewChat}
          className={`flex items-center justify-center gap-2 p-3 rounded-full mb-8 transition-colors ${darkMode ? 'bg-[#2c2d2e] hover:bg-[#374151]' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'}`}
        >
          <Plus size={20} /> <span className="text-sm font-medium">New Chat</span>
        </button>

        <div className="flex-1 overflow-y-auto space-y-0.1">
          <p className="text-xs font-semibold text-gray-500 px-2 mb-2 uppercase tracking-wider">Recent</p>
          {chats.map(chat => (
            <button
              key={chat.id}
              onClick={() => { setActiveChatId(chat.id); setShowLimitError(false); setIsSidebarOpen(false); setCurrentView('chat'); }}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-sm transition-colors ${activeChatId === chat.id && currentView === 'chat' ? (darkMode ? 'bg-[#004a77] text-blue-200' : 'bg-blue-100 text-blue-700 font-semibold') : 'hover:bg-opacity-10 hover:bg-gray-500'}`}
            >
              <MessageSquare size={16} />
              <span className="truncate">{chat.title}</span>
            </button>
          ))}
        </div>

        <div className={`border-t pt-3 flex flex-col gap-1 ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>

          {/* Settings & Help Dropdown */}
          <div className="relative">
            {showSettings && (
              <div className={`absolute bottom-full left-0 w-full mb-2 rounded-xl shadow-2xl border overflow-hidden transition-all duration-200 ${darkMode ? 'bg-[#1e1f20] border-gray-700' : 'bg-white border-gray-200'
                }`}>

                {/* Dark/Light Mode inside menu */}
                <button
                  onClick={() => { toggleDarkMode(); setShowSettings(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-xs font-medium transition-colors ${darkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                    }`}
                >
                  {darkMode ? <Sun size={14} className="text-yellow-400" /> : <Moon size={14} className="text-indigo-600" />}
                  <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>

                <div className={`h-[1px] mx-2 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />

                {/* Links - Scaled down to 12px (text-xs) */}
                {[
                  { label: 'About', icon: <Info size={14} />, url: 'https://janbharatai.com/about' },
                  { label: 'Privacy', icon: <Shield size={14} />, url: 'https://janbharatai.com/privacy' },
                  { label: 'T&C', icon: <FileText size={14} />, url: 'https://janbharatai.com/terms' },
                  { label: 'Contact', icon: <Mail size={14} />, url: 'https://janbharatai.com/contact' }
                ].map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 px-3 py-2 text-xs transition-colors ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                      }`}>
                    {link.icon}
                    <span>{link.label}</span>
                  </a>
                ))}
              </div>
            )}

            {/* The main toggle - Matches existing sidebar buttons */}
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors mb-1 ${darkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                }`}
            >
              <div className="flex items-center gap-3 text-xs font-medium">
                <Settings size={14} />
                <span>Settings & Help</span>
              </div>
              <ChevronDown size={12} className={`transition-transform duration-200 ${showSettings ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Share Button - Compact version */}
          <button onClick={handleShare} className={`flex items-center gap-3 px-3 py-2 text-xs font-medium rounded-lg transition-colors mb-1 ${darkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
            }`}>
            <Share size={14} />
            <span>Share JanBharatAI</span>
          </button>

          {/* Get Premium / Active Plan Button */}
          <button
            onClick={() => { setCurrentView('subscription'); setIsSidebarOpen(false); }}
            className={`flex items-center gap-3 px-3 py-2 text-xs font-bold rounded-lg transition-all mb-1 ${currentView === 'subscription'
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
              : (darkMode ? 'hover:bg-gray-800 text-yellow-500' : 'hover:bg-gray-100 text-blue-600')}`}
          >
            <Crown size={14} className={currentView === 'subscription' ? 'text-white' : 'text-yellow-500'} />
            <span>{isPremium ? 'Active Plan' : 'Get Premium'}</span>
          </button>

          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center border relative ${darkMode ? 'bg-[#2c2d2e] border-gray-700' : 'bg-white border-gray-300'}`}>
                {user.photoURL ? (
                  <img src={user.photoURL} className="w-7 h-7 rounded-full" alt="User" referrerPolicy="no-referrer" />
                ) : <User size={14} />}
                {isPremium && (
                  <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full p-0.5 border border-white dark:border-zinc-900 shadow-sm">
                    <Crown size={8} className="text-white" />
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] truncate max-w-[80px] font-semibold tracking-tight leading-none mb-0.5">{user?.displayName || 'User'}</span>
                {isPremium && <span className="text-[8px] text-yellow-500 font-bold uppercase tracking-widest leading-none">Premium</span>}
              </div>
            </div>
            <LogOut size={16} className="cursor-pointer hover:text-red-500 transition-colors opacity-70 hover:opacity-100" onClick={handleLogout} />
          </div>
        </div>
        {/* Copied to Clipboard Toast */}
        {/* Compact Toast positioned relative to the input container */}
      </div>

      <div className="flex-1 flex flex-col relative h-full">
        <header className={`p-4 flex justify-between items-center border-b ${darkMode ? 'border-gray-800 bg-[#131314]' : 'border-gray-200 bg-white'} md:hidden`}>
          <Menu size={24} className="cursor-pointer" onClick={() => setIsSidebarOpen(true)} />

          <div className="flex items-center gap-2">
            <img
              src={logo}
              alt="Logo"
              className="w-6 h-6"
            />
            <h1 className="text-xl font-bold text-transparent bg-gradient-to-r from-orange-500 via-stone-500 to-green-600 bg-clip-text font-display tracking-tight">
              JanBharatAI
            </h1>
          </div>

          <div className="relative">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${darkMode ? 'bg-[#2c2d2e] border-gray-700' : 'bg-white border-gray-300'} overflow-hidden`}>
              {user.photoURL ? (
                <img src={user.photoURL} className="w-full h-full object-cover" alt="U" referrerPolicy="no-referrer" />
              ) : <User size={16} className={darkMode ? "text-gray-400" : "text-gray-600"} />}
            </div>
            {isPremium && (
              <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full p-0.5 border border-white dark:border-zinc-900 shadow-sm z-10">
                <Crown size={8} className="text-white" />
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
          {currentView === 'chat' ? (
            <div className="flex-1 p-4 md:p-8 flex flex-col">
              <div className="max-w-3xl mx-auto space-y-6 w-full">
                {activeChat.messages.length === 0 && (
                  <div className="mt-10 md:mt-20 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-orange-500 via-stone-500 to-green-600 bg-clip-text font-display tracking-tight px-4">Namaste, {user?.displayName?.split(' ')[0] || 'User'}.</h2>
                    <p className="text-gray-500 mt-4 text-lg md:text-xl font-light text-transparent bg-gradient-to-r from-orange-500 via-stone-500 to-green-600 bg-clip-text font-display tracking-tight">JanBharatAI: Know Bharat Better.</p>
                    <p className="text-gray-500 mt-4 text-lg font-bold md:text-xl text-transparent bg-gradient-to-r from-orange-500 via-stone-500 to-green-600 bg-clip-text font-display tracking-tight">The only GenAI platform built exclusively on data.gov.in and myscheme.gov.in. Get verified statistics about India and 100% accurate scheme eligibility.</p>
                    <div className="mt-8 space-y-10 animate-fadeIn">
                      {/* Easy-to-Understand Suggestions */}
                      <div className="flex flex-col items-center gap-6">
                        <p className={`text-xs uppercase tracking-[0.2em] font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          Try asking me
                        </p>
                        <div className="flex flex-wrap justify-center gap-4 max-w-3xl">
                          {[
                            "How many schools in Bihar have drinking water?",
                            "How has the forest area in Odisha changed?",
                            "Compare school enrollment in Kerala vs Bihar.",
                            "Which state has the most electric cars?",
                            "What is the average income in Goa?"
                          ].map((suggestion, idx) => (
                            <span
                              key={idx}
                              className={`px-5 py-2.5 rounded-2xl text-sm md:text-base border transition-all cursor-pointer ${darkMode
                                ? 'border-gray-700 bg-gray-800/50 text-gray-300 hover:border-blue-500 hover:text-blue-400'
                                : 'border-gray-200 bg-white text-gray-700 shadow-sm hover:border-blue-400 hover:text-blue-600'
                                }`}
                              onClick={() => setInput(suggestion)}
                            >
                              {suggestion}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeChat.messages.map((m, i) => (
                  <div key={i} className={`flex gap-3 md:gap-4 p-4 rounded-2xl ${m.role === 'user'
                    ? (darkMode
                      ? 'bg-[#2c2d2e] text-gray-200 ml-auto max-w-[90%] md:max-w-[85%]'
                      : 'bg-[#f0f2f5] text-black ml-auto max-w-[90%] md:max-w-[85%]')
                    : 'max-w-full'
                    }`}>
                    {/* This line restores the Bot icon for responses */}
                    {m.role === 'bot' && <Bot className="text-blue-500 mt-1 shrink-0 hidden sm:block" />}

                    <div className="flex flex-col w-full">
                      <div className={`prose prose-sm md:prose-base max-w-none overflow-x-auto ${darkMode
                        ? 'prose-invert'
                        : 'prose-black !text-black'
                        }`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => <p className={darkMode ? "" : "text-black mb-4"}>{children}</p>,
                            li: ({ children }) => <li className={darkMode ? "" : "text-black"}>{children}</li>,
                            a: ({ node, children, ...props }) => (
                              <a
                                {...props}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${darkMode ? "text-blue-400" : "text-blue-600"} font-bold break-words`}
                                style={{ wordBreak: 'break-word' }}
                              >
                                {children}
                              </a>
                            )
                          }}
                        >
                          {m.text}
                        </ReactMarkdown>
                      </div>

                      {/* Action Buttons for Bot Responses */}
                      {m.role === 'bot' && (
                        <div className="flex gap-2 mt-4">
                          {/* Copy Button */}
                          <button
                            onClick={() => copyToClipboard(m.text)}
                            className={`flex items-center gap-2 text-xs font-medium w-fit px-3 py-1.5 rounded-lg transition-all ${darkMode
                              ? 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                              : 'bg-gray-100 text-gray-500 hover:text-black hover:bg-gray-200'
                              }`}
                          >
                            <Copy size={14} /> <span>Copy</span>
                          </button>

                          {/* Share Button */}
                          <button
                            onClick={() => shareMessage(m.text)}
                            className={`flex items-center gap-2 text-xs font-medium w-fit px-3 py-1.5 rounded-lg transition-all ${darkMode
                              ? 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                              : 'bg-gray-100 text-gray-500 hover:text-black hover:bg-gray-200'
                              }`}
                          >
                            <Share size={14} /> <span>Share</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-8 animate-fadeIn">
                    {/* Dynamic pulsing spinner that changes size and glow */}
                    <div className="relative mb-6">
                      <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                      <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-purple-500 rounded-full animate-ping opacity-30"></div>
                    </div>

                    {/* Engaging loading text with a typewriter/pulse effect */}
                    <div className="flex flex-col items-center gap-2">
                      <p className={`text-sm md:text-base font-medium text-center transition-all duration-500 animate-pulse ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {loadingText}
                      </p>
                      <div className="flex gap-1">
                        <span className="w-1 h-1 rounded-full bg-blue-500 animate-bounce [animation-delay:-0.3s]"></span>
                        <span className="w-1 h-1 rounded-full bg-purple-500 animate-bounce [animation-delay:-0.15s]"></span>
                        <span className="w-1 h-1 rounded-full bg-blue-500 animate-bounce"></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </div>
          ) : (
            <div className="flex-1 w-full max-w-6xl mx-auto">
              <Subscription user={user} activeSubscription={activeSubscription} />
            </div>
          )}
        </main>

        {currentView === 'chat' && (
          <footer className={`p-4 md:p-6 ${darkMode ? 'bg-[#131314]' : 'bg-white'} border-t md:border-none relative`}>
            {/* Subtle Toast positioned exactly above the prompt box */}
            {showCopyToast && (
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-[100] animate-fadeIn pointer-events-none">
                <div className={`px-2 py-1 rounded-md border shadow-sm backdrop-blur-md ${darkMode
                  ? 'bg-[#2c2d2e]/90 border-gray-700 text-gray-400'
                  : 'bg-[#1f1f1f]/90 border-transparent text-gray-200'
                  }`}>
                  <p className="text-[10px] font-medium tracking-wide whitespace-nowrap">
                    Copied to clipboard
                  </p>
                </div>
              </div>
            )}
            {showLimitError && (
              <div className="max-w-3xl mx-auto mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 text-red-500 backdrop-blur-sm animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/20 rounded-full">
                    <AlertTriangle size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-bold">Daily Limit Reached.</p>
                    <p className="text-xs opacity-80">Continue your work instantly with a subsidized plan for only ₹9.</p>
                  </div>
                </div>
                <button
                  onClick={() => { setCurrentView('subscription'); setShowLimitError(false); }}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-black rounded-xl shadow-lg hover:scale-105 transition-all w-full md:w-auto uppercase tracking-wider"
                >
                  Get Premium
                </button>
              </div>
            )}
            <div className={`max-w-3xl mx-auto flex items-center gap-3 p-4 rounded-2xl shadow-2xl border transition-all duration-300 ${darkMode ? 'bg-[#1e1f20] border-gray-700' : 'bg-white border-gray-200'}`}>
              <input
                className={`flex-1 bg-transparent border-none focus:ring-0 outline-none text-sm md:text-base ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                placeholder="Search 4lakh+ Govt datasets or check scheme eligibility..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              />
              <Send
                size={20}
                className={`transition-colors ${input && !loading ? 'text-blue-500 cursor-pointer' : 'text-gray-400 cursor-default'}`}
                onClick={loading ? null : sendMessage}
              />
            </div>
            <p className="text-[9px] md:text-[10px] text-gray-500 text-center mt-3 uppercase tracking-widest font-bold">JanBharatAI can make mistakes. Verify important information.</p>
          </footer>
        )}
      </div>
    </div>
  );
}